import React, { useState } from 'react';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Avatar,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUsers } from '../hooks/useUsers';
import PageContainer from '../components/common/PageContainer';
import SectionHeader from '../components/common/SectionHeader';
import DataTable from '../components/common/DataTable';
import StatusChip from '../components/common/StatusChip';

const Users = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const {
    users,
    loading,
    error,
    success,
    setError,
    setSuccess,
    createUser,
    updateUser,
    deleteUser,
    count,
    page,
    setPage,
    searchTerm,
    setSearchTerm,
  } = useUsers();

  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    whatsapp: '',
    password: '',
    is_active: true,
    is_staff: false,
  });

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username || '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        whatsapp: user.whatsapp || '',
        password: '',
        is_active: user.is_active,
        is_staff: user.is_staff,
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        whatsapp: '',
        password: '',
        is_active: true,
        is_staff: false,
      });
    }
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (!data.password) delete data.password;

      if (editingUser) {
        await updateUser(editingUser.id, data);
      } else {
        await createUser(data);
      }

      setTimeout(handleCloseDialog, 1000);
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      try {
        await deleteUser(userId);
      } catch (err) {
        // Error handled by hook
      }
    }
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  };

  const filteredUsers = users;

  const columns = [
    {
      id: 'user',
      label: 'Usuario',
      render: (user) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#800020', width: 40, height: 40, fontSize: '0.875rem', fontWeight: 600 }}>
            {getInitials(user.first_name, user.last_name)}
          </Avatar>
          <Box>
            <Typography sx={{ color: '#0e141b', fontWeight: 600, fontSize: '0.875rem' }}>
              {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username}
            </Typography>
            <Typography sx={{ color: '#4e7397', fontSize: '0.75rem' }}>{user.email}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'role',
      label: 'Rol',
      render: (user) => <StatusChip status={user.is_staff} type="user-role" />,
    },
    {
      id: 'status',
      label: 'Estado',
      render: (user) => <StatusChip status={user.is_active} type="active" />,
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      render: (user) => <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>{user.whatsapp || '-'}</Typography>,
    },
  ];

  const breadcrumbs = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Administración', path: '#' },
    { label: 'Usuarios', path: '/users' },
  ];

  const actions = [
    {
      label: 'Nuevo Usuario',
      icon: <AddIcon />,
      onClick: () => handleOpenDialog(),
    },
  ];

  const activeUsersCount = users.filter((u) => u.is_active).length;
  const staffUsersCount = users.filter((u) => u.is_staff).length;

  return (
    <PageContainer onLogout={() => { logout(); navigate('/login'); }}>
      <SectionHeader
        title="Gestión de Usuarios"
        subtitle="Administra el acceso y los roles del personal de Frigorífico Capriata."
        breadcrumbs={breadcrumbs}
        actions={actions}
      />

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          { label: 'Usuarios Activos', value: activeUsersCount, icon: <AddIcon />, color: '#800020' },
          { label: 'Total de Usuarios', value: users.length, icon: <AddIcon />, color: '#2e7d32' },
          { label: 'Administradores', value: staffUsersCount, icon: <AddIcon />, color: '#800020' },
        ].map((stat, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Card sx={{ borderRadius: '0.75rem', border: '1px solid #d0dbe7', boxShadow: 'none' }}>
              <CardContent>
                <Box sx={{ p: 1, bgcolor: stat.color, borderRadius: '0.5rem', display: 'inline-flex', mb: 2 }}>
                  {React.cloneElement(stat.icon, { sx: { color: '#ffffff', fontSize: '1.5rem' } })}
                </Box>
                <Typography variant="h4" sx={{ color: '#0e141b', fontWeight: 700, mb: 0.5 }}>{stat.value}</Typography>
                <Typography sx={{ color: '#4e7397', fontSize: '0.875rem', fontWeight: 500 }}>{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Alerts (Visible when dialog is closed) */}
      {!openDialog && error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {!openDialog && success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <DataTable
        columns={columns}
        rows={filteredUsers}
        loading={loading}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Buscar por nombre, email o ID..."
        count={count}
        page={page}
        rowsPerPage={20}
        onPageChange={(e, newPage) => setPage(newPage)}
        actions={(user) => (
          <>
            <IconButton size="small" onClick={() => handleOpenDialog(user)} sx={{ color: '#4e7397', '&:hover': { color: '#800020', bgcolor: 'rgba(128, 0, 32, 0.04)' } }}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleDelete(user.id)} sx={{ color: '#4e7397', '&:hover': { color: '#d32f2f', bgcolor: 'rgba(211, 47, 47, 0.04)' } }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      />

      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ fontWeight: 600, color: '#0e141b' }}>
            {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
          </DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            <TextField margin="dense" label="Usuario" fullWidth required value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} sx={{ mb: 2 }} />
            <TextField margin="dense" label="Email" type="email" fullWidth required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} sx={{ mb: 2 }} />
            <TextField margin="dense" label="Nombre" fullWidth value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} sx={{ mb: 2 }} />
            <TextField margin="dense" label="Apellido" fullWidth value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} sx={{ mb: 2 }} />
            <TextField margin="dense" label="WhatsApp" fullWidth value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })} sx={{ mb: 2 }} helperText="Formato: +5491123456789" />
            <TextField margin="dense" label={editingUser ? 'Nueva Contraseña (opcional)' : 'Contraseña'} type="password" fullWidth required={!editingUser} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select margin="dense" label="Activo" fullWidth SelectProps={{ native: true }} value={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}>
                <option value={true}>Sí</option>
                <option value={false}>No</option>
              </TextField>
              <TextField select margin="dense" label="Staff" fullWidth SelectProps={{ native: true }} value={formData.is_staff} onChange={(e) => setFormData({ ...formData, is_staff: e.target.value === 'true' })}>
                <option value={true}>Sí</option>
                <option value={false}>No</option>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} sx={{ color: '#4e7397' }}>Cancelar</Button>
            <Button type="submit" variant="contained" sx={{ bgcolor: '#800020', '&:hover': { bgcolor: '#600010' } }}>{editingUser ? 'Actualizar' : 'Crear'}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </PageContainer>
  );
};

export default Users;
